import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@healthinal/ui';
import isEmail from 'is-email';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCreatePatientBezugspersonMutation } from '../../../api/created/bezugsperson';
import {
  IBezugsperson,
  ICreateBezugspersonPayload,
  useCreateBezugspersonMutation,
  useGetBezugspersonByIdQuery,
  useUpdateBezugspersonMutation,
} from '../../../api/generated/bezugperson-controller/bezugperson-controller';
import { ReferencePersonDto } from '../../../api/generated/model/bezugspersonDto';
import { isPhoneNumberValid } from '../../../utils/phoneNumber';

export type ReferencePersonCreateEndpoint = 'person' | 'patient';

interface FormValues {
  salutation: string;
  firstName: string;
  lastName: string;
  function: string;
  phone: string;
  email: string;
}

type CreateModeProps = {
  mode: 'create';
  open: boolean;
  onClose: (referencePerson?: ReferencePersonDto) => void;
};

type EditModeProps = {
  mode: 'edit';
  open: boolean;
  personId: string;
  createEndpoint?: ReferencePersonCreateEndpoint;
  onClose: (referencePerson?: ReferencePersonDto) => void;
};

type UpdateModeProps = {
  mode: 'update';
  open: boolean;
  personId: string;
  referencePersonId: string;
  onClose: (referencePerson?: ReferencePersonDto) => void;
};

type Props = CreateModeProps | EditModeProps | UpdateModeProps;

const EMPTY_FORM_VALUES: FormValues = {
  salutation: '',
  firstName: '',
  lastName: '',
  function: '',
  phone: '',
  email: '',
};

function bezugspersonToFormValues(bezugsperson: IBezugsperson): FormValues {
  return {
    salutation: bezugsperson.salutation ?? '',
    firstName: bezugsperson.firstName,
    lastName: bezugsperson.lastName,
    function: bezugsperson.position ?? '',
    phone: bezugsperson.phoneNumber ?? '',
    email: bezugsperson.email ?? '',
  };
}

export function CreateReferencePersonDialog(props: Props) {
  const { open, onClose } = props;
  const isUpdateMode = props.mode === 'update';
  const updatePersonId = isUpdateMode ? props.personId : '';
  const updateReferencePersonId = isUpdateMode ? props.referencePersonId : '';

  const createBezugspersonMutation = useCreateBezugspersonMutation();
  const createPatientBezugspersonMutation = useCreatePatientBezugspersonMutation();
  const updateBezugspersonMutation = useUpdateBezugspersonMutation();
  const {
    data: bezugsperson,
    isFetching: isFetchingBezugsperson,
    isError: isFetchBezugspersonError,
  } = useGetBezugspersonByIdQuery(updatePersonId, updateReferencePersonId, open && isUpdateMode);

  const isCreatingReferencePerson =
    props.mode === 'edit' &&
    (props.createEndpoint === 'patient'
      ? createPatientBezugspersonMutation.isPending
      : createBezugspersonMutation.isPending);
  const isUpdatingReferencePerson = isUpdateMode && updateBezugspersonMutation.isPending;
  const isSavingReferencePerson = isCreatingReferencePerson || isUpdatingReferencePerson;

  const createReferencePersonError =
    props.mode === 'edit' &&
    (props.createEndpoint === 'patient'
      ? createPatientBezugspersonMutation.isError
      : createBezugspersonMutation.isError);
  const updateReferencePersonError = isUpdateMode && updateBezugspersonMutation.isError;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<FormValues>({
    defaultValues: EMPTY_FORM_VALUES,
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open) return;

    if (isUpdateMode) {
      if (bezugsperson) {
        reset(bezugspersonToFormValues(bezugsperson));
      }
      return;
    }

    reset(EMPTY_FORM_VALUES);
  }, [open, reset, isUpdateMode, bezugsperson]);

  const handleClose = () => {
    if (isSavingReferencePerson) return;

    reset(EMPTY_FORM_VALUES);
    onClose();
  };

  const dialogTitle =
    props.mode === 'update' ? 'Bezugsperson bearbeiten' : 'Bezugsperson erstellen';

  const isSubmitDisabled =
    isUpdateMode
      ? !isDirty || !isValid || isFetchingBezugsperson || isUpdatingReferencePerson || !bezugsperson
      : !isDirty || !isValid || isCreatingReferencePerson;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ width: 'min(600px, 95vw)' }}>
        <ModalClose />
        <Typography level="h4">{dialogTitle}</Typography>

        <form
          onSubmit={(event) => {
            event.stopPropagation();

            void handleSubmit(async (data) => {
              if (props.mode === 'update') {
                if (!bezugsperson) return;

                const updatedReferencePerson = await updateBezugspersonMutation.mutateAsync({
                  personId: props.personId,
                  id: props.referencePersonId,
                  data: {
                    id: bezugsperson.id,
                    relationType: 'BEZUGSPERSON',
                    salutation: data.salutation || undefined,
                    firstName: data.firstName.trim(),
                    lastName: data.lastName.trim(),
                    position: data.function.trim() || '',
                    phoneNumber: data.phone.trim() || '',
                    email: data.email.trim() || '',
                    isSelected: bezugsperson.isSelected,
                  },
                });

                reset(EMPTY_FORM_VALUES);
                onClose(updatedReferencePerson);
                return;
              }

              if (props.mode === 'edit') {
                const payload = {
                  salutation: data.salutation || undefined,
                  firstName: data.firstName.trim(),
                  lastName: data.lastName.trim(),
                  position: data.function.trim() || undefined,
                  phoneNumber: data.phone.trim() || undefined,
                  email: data.email.trim() || undefined,
                } as ICreateBezugspersonPayload & { salutation?: string };

                const createdReferencePerson =
                  props.createEndpoint === 'patient'
                    ? await createPatientBezugspersonMutation.mutateAsync({
                        patientId: props.personId,
                        data: payload,
                      })
                    : await createBezugspersonMutation.mutateAsync({
                        personId: props.personId,
                        data: payload,
                      });

                reset(EMPTY_FORM_VALUES);
                onClose(createdReferencePerson);
                return;
              }

              const localReferencePerson: ReferencePersonDto & { salutation?: string } = {
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                position: data.function.trim() || '',
                phoneNumber: data.phone.trim() || '',
                email: data.email.trim() || '',
                isSelected: true,
                salutation: data.salutation || '',
              };

              reset(EMPTY_FORM_VALUES);
              onClose(localReferencePerson);
            })(event);
          }}>
          <Stack gap={2} sx={{ mt: 2 }}>
            <FormControl>
              <FormLabel>Anrede</FormLabel>
              <Controller
                name="salutation"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select
                    onChange={(_, value) => onChange(value)}
                    value={value}
                    placeholder="Bitte auswählen"
                    disabled={isUpdateMode && isFetchingBezugsperson}>
                    <Option value="MR">Herr</Option>
                    <Option value="MRS">Frau</Option>
                  </Select>
                )}
              />
            </FormControl>

            <FormControl error={!!errors.firstName}>
              <FormLabel>Vorname</FormLabel>
              <Input
                {...register('firstName', { required: true })}
                disabled={isUpdateMode && isFetchingBezugsperson}
              />
              {errors.firstName ? <FormHelperText>Vorname ist erforderlich</FormHelperText> : null}
            </FormControl>

            <FormControl error={!!errors.lastName}>
              <FormLabel>Nachname</FormLabel>
              <Input
                {...register('lastName', { required: true })}
                disabled={isUpdateMode && isFetchingBezugsperson}
              />
              {errors.lastName ? <FormHelperText>Nachname ist erforderlich</FormHelperText> : null}
            </FormControl>

            <FormControl>
              <FormLabel>Funktion</FormLabel>
              <Input
                {...register('function')}
                disabled={isUpdateMode && isFetchingBezugsperson}
              />
            </FormControl>

            <FormControl error={!!errors.phone}>
              <FormLabel>Telefon Nr.</FormLabel>
              <Input
                {...register('phone', {
                  validate: (phone) => !phone || isPhoneNumberValid(phone),
                })}
                type="tel"
                disabled={isUpdateMode && isFetchingBezugsperson}
              />
              {errors.phone ? <FormHelperText>Diese Telefon Nr. ist nicht korrekt</FormHelperText> : null}
            </FormControl>

            <FormControl error={!!errors.email}>
              <FormLabel>E-Mail</FormLabel>
              <Input
                {...register('email', {
                  validate: (email) => !email || isEmail(email),
                })}
                type="email"
                disabled={isUpdateMode && isFetchingBezugsperson}
              />
              {errors.email ? <FormHelperText>Diese E-Mail-Adresse ist nicht korrekt</FormHelperText> : null}
            </FormControl>

            {isFetchBezugspersonError ? (
              <FormHelperText>Bezugsperson konnte nicht geladen werden.</FormHelperText>
            ) : null}

            {createReferencePersonError ? (
              <FormHelperText>Bezugsperson konnte nicht erstellt werden.</FormHelperText>
            ) : null}

            {updateReferencePersonError ? (
              <FormHelperText>Bezugsperson konnte nicht bearbeitet werden.</FormHelperText>
            ) : null}

            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button color="neutral" onClick={handleClose} disabled={isSavingReferencePerson}>
                Abbrechen
              </Button>

              <Button
                type="submit"
                disabled={isSubmitDisabled}
                loading={isSavingReferencePerson || (isUpdateMode && isFetchingBezugsperson)}>
                Speichern
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
